import {
  AlertTriangle,
  BarChart3,
  CheckCircle2,
  DollarSign,
  Gauge,
  Sigma,
  TrendingUp,
  Truck,
  Users,
} from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../../../../../components/ui/accordion";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../../../../components/ui/card";
import { formatDateBR } from "../../../../../utils/format-utils";
import { ImileCheckoutItem } from "../types";
import { CheckoutStatCard } from "../../entrego-resolution/components/checkout-stat-card";
import { ProcessedFilesTable } from "../../entrego-resolution/components/processed-files-table";
import { CoerenciaTable } from "./coerencia-table";
import { ImileCheckoutActionButtons } from "./imile-checkout-action-buttons";

// Abaixo disso a distribuição não diz nada: com 1 a 4 entregadores os quartis e
// o limite de Tukey são ruído, e mostrar "mediana" de 2 pessoas engana mais do
// que informa.
const MINIMO_ENTREGADORES_PARA_METRICAS = 5;

interface ImileCheckoutItemCardProps {
  checkout: ImileCheckoutItem;
  onConfirmCheckout: (filterDate: string, ops: string) => void;
  onStartDoc: (filterDate: string, ops: string) => void;
  onSubmitRemessa: (filterDate: string, ops: string) => void;
  isConfirming: boolean;
  isStartingDoc: boolean;
  isSubmittingRemessa: boolean;
}

export function ImileCheckoutItemCard({
  checkout,
  onConfirmCheckout,
  onStartDoc,
  onSubmitRemessa,
  isConfirming,
  isStartingDoc,
  isSubmittingRemessa,
}: ImileCheckoutItemCardProps) {
  const dateCheckout = checkout.date_to_checkout.slice(0, 10);
  const temFranquias = (checkout.filesProcessed?.length ?? 0) > 0;
  // O backend já devolve só os suspeitos, então o tamanho da lista É a contagem.
  const suspeitos = checkout.coerencia ?? [];
  const temCoerencia = suspeitos.length > 0;
  // Pago sem nenhuma leitura é o caso mais grave: destacado separado.
  const semLeituras = suspeitos.filter((l) =>
    l.motivos.includes("sem_leituras")
  ).length;

  const temMetricas =
    (checkout.entregadores_com_ticket ?? 0) >=
    MINIMO_ENTREGADORES_PARA_METRICAS;

  return (
    <Card className="mb-6">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg">
            Checkout - {formatDateBR(dateCheckout)}
          </CardTitle>
          <ImileCheckoutActionButtons
            status={checkout.status}
            ops={checkout.ops}
            date={dateCheckout}
            onConfirmCheckout={() =>
              onConfirmCheckout(dateCheckout, checkout.ops)
            }
            onStartDoc={() => onStartDoc(dateCheckout, checkout.ops)}
            onSubmitRemessa={() => onSubmitRemessa(dateCheckout, checkout.ops)}
            isConfirming={isConfirming}
            isStartingDoc={isStartingDoc}
            isSubmittingRemessa={isSubmittingRemessa}
          />
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
          <CheckoutStatCard
            title="Operação"
            value={checkout.ops}
            icon={Truck}
            valueType="text"
          />
          <CheckoutStatCard
            title="Entregadores"
            value={checkout.entregadores_unicos ?? 0}
            icon={Users}
            valueType="integer"
          />
          <CheckoutStatCard
            title="Ticket Médio"
            value={checkout.ticket_medio}
            icon={TrendingUp}
            valueType="cents"
          />
          <CheckoutStatCard
            title="Valor de Face Acumulado"
            value={checkout.total_valor_face}
            icon={DollarSign}
            valueType="cents"
          />
        </div>

        {/*
          Fila separada da de cima de propósito: os dois "ticket médio" são
          grandezas diferentes — o de cima é valor de face ÷ entregadores, o
          daqui é valor ÷ pacote. Juntar na mesma fila garante erro de leitura.
        */}
        {temMetricas && (
          <div className="mb-4 rounded-lg border bg-muted/20 p-4">
            <div className="mb-3 flex items-center justify-between">
              <h3 className="text-sm font-semibold text-foreground">
                Ticket por entregador (valor ÷ pacotes)
              </h3>
              <span className="text-xs text-muted-foreground">
                {checkout.entregadores_com_ticket} entregadores com pacote e
                valor
              </span>
            </div>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
              <CheckoutStatCard
                title="Ticket médio"
                value={checkout.ticket_medio_entregador ?? 0}
                icon={Sigma}
                valueType="cents"
              />
              <CheckoutStatCard
                title="Mediana"
                value={checkout.ticket_mediana ?? 0}
                icon={BarChart3}
                valueType="cents"
              />
              <CheckoutStatCard
                title="Q3"
                value={checkout.ticket_q3 ?? 0}
                icon={Gauge}
                valueType="cents"
              />
              <CheckoutStatCard
                title="Limite superior (Tukey)"
                value={checkout.ticket_limite_superior ?? 0}
                icon={AlertTriangle}
                valueType="cents"
              />
            </div>
            <p className="mt-3 text-xs text-muted-foreground">
              Limite superior = Q3 + 1,5 × (Q3 − Q1). A iMile paga tarifa fixa
              por pacote, então o normal é mediana e Q3 coincidirem — quem passa
              do limite está fora da tarifa e aparece abaixo.
            </p>
          </div>
        )}

        {temCoerencia ? (
          <div
            className={`mb-4 flex items-center gap-2 rounded-lg border px-4 py-3 text-sm ${
              semLeituras > 0
                ? "border-red-300 bg-red-50 text-red-900"
                : "border-amber-300 bg-amber-50 text-amber-900"
            }`}
          >
            <AlertTriangle className="h-4 w-4 shrink-0" />
            <span>
              <strong>{suspeitos.length}</strong>{" "}
              {suspeitos.length === 1 ? "entregador" : "entregadores"} para
              conferir
              {checkout.entregadores_conferidos
                ? ` de ${checkout.entregadores_conferidos}`
                : ""}
              {semLeituras > 0 && (
                <>
                  , sendo <strong>{semLeituras}</strong> pago
                  {semLeituras === 1 ? "" : "s"} sem nenhuma leitura da iMile
                </>
              )}
              . Veja em &quot;Conferência financeiro vs pacotes&quot; antes de
              aprovar a compra.
            </span>
          </div>
        ) : (
          checkout.entregadores_conferidos != null &&
          checkout.entregadores_conferidos > 0 && (
            <div className="mb-4 flex items-center gap-2 rounded-lg border border-green-300 bg-green-50 px-4 py-3 text-sm text-green-900">
              <CheckCircle2 className="h-4 w-4 shrink-0" />
              <span>
                Financeiro e leituras da iMile batem para os{" "}
                <strong>{checkout.entregadores_conferidos}</strong> entregadores
                do dia.
              </span>
            </div>
          )
        )}

        {(temFranquias || temCoerencia) && (
          <Accordion type="single" collapsible className="mt-4">
            {temFranquias && (
              <AccordionItem value="files">
                <AccordionTrigger>
                  Arquivos processados Franqueados dia: {dateCheckout}
                </AccordionTrigger>
                <AccordionContent>
                  <ProcessedFilesTable files={checkout.filesProcessed!} />
                </AccordionContent>
              </AccordionItem>
            )}

            {temCoerencia && (
              <AccordionItem value="coerencia">
                <AccordionTrigger>
                  Conferência financeiro vs pacotes dia: {dateCheckout}
                  <span
                    className={`ml-2 rounded-full px-2 py-0.5 text-xs font-medium ${
                      semLeituras > 0
                        ? "bg-red-100 text-red-800"
                        : "bg-amber-100 text-amber-800"
                    }`}
                  >
                    {suspeitos.length} a conferir
                  </span>
                </AccordionTrigger>
                <AccordionContent>
                  <CoerenciaTable
                    rows={suspeitos}
                    conferidos={checkout.entregadores_conferidos ?? undefined}
                  />
                </AccordionContent>
              </AccordionItem>
            )}
          </Accordion>
        )}
      </CardContent>
    </Card>
  );
}
